/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useServiceContext } from '../../../root'
import { AclLevel } from '../../../constants/fsi'
import type { FileWithMetadata } from './types'

interface SortableDocumentItemProps {
  doc: FileWithMetadata
  index: number
  isUploadAndFile?: boolean
  removeFile: (index: number) => void
  preview: (doc: FileWithMetadata) => void
}

const SortableDocumentItem: React.FC<SortableDocumentItemProps> = ({
  doc,
  index,
  isUploadAndFile,
  removeFile,
  preview
}) => {
  const context = useServiceContext()
  const { translate } = context.service.filter

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: doc.id || `file-${index}`
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab'
  }

  return (
    <div ref={setNodeRef} style={style} className="transfer-box" {...attributes} {...listeners}>
      <div className="drag-handle-icon">══</div>
      <div className="transfer-filename row">
        <div
          className={`max-width-600 ellipsis ${
            doc.checksum?.existFiles === null ? 'mb15' : ''
          } ${isUploadAndFile ? 'col-sm-12' : 'col-sm-6'}`}
        >
          <a
            className="cancel-file-upload"
            onClick={(e) => {
              e.stopPropagation()
              removeFile(index)
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          ></a>
          <span className="hover" title={doc?.relativePath || ''}>
            {doc?.relativePath}
          </span>
        </div>
        {doc?.checksumValidate && (
          <div className={`col-sm-${isUploadAndFile ? '12' : '6'} ellipsis d-inline-flex`}>
            <div className="mt2 mr4">
              <strong>{translate('DUPLICATE_DETECTION_FAILED')}</strong>
            </div>
            <div className="mt2">
              <p>{doc?.checksumValidate}</p>
            </div>
            <div>
              <span className="warning-file-upload"></span>
            </div>
          </div>
        )}
        {doc.checksum?.existFiles && doc.checksum.existFiles.length > 0 && (
          <div className={`col-sm-${isUploadAndFile ? '12' : '6'}`}>
            <div className="d-inline-flex">
              <div className="mt2 mr4">
                <strong>{translate('DUPLICATE_DETECTED')}</strong>
              </div>
              {doc.checksum.existFiles.length > 1 && (
                <div className="mt2">
                  <a
                    onClick={(e) => {
                      e.stopPropagation()
                      preview(doc)
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    className="cursor-pointer"
                  >
                    {translate('WORKFLOW_MULTIPLE_DOCS')}
                  </a>
                </div>
              )}
              {doc.checksum?.existFiles.length === 1 && (
                <div className="mt2">
                  {doc.checksum?.existFiles[0].level !== AclLevel.BROWSE &&
                    doc.checksum?.existFiles[0].level !== AclLevel.NONE && (
                      <a
                        className={
                          doc.checksum?.existFiles[0].fileName.length > 37
                            ? 'trunk-link hover cursor-pointer'
                            : 'hover cursor-pointer'
                        }
                        onClick={(e) => {
                          e.stopPropagation()
                          preview(doc)
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        title={doc.checksum?.existFiles[0].fileName}
                      >
                        {doc?.checksum?.existFiles[0]?.fileName}
                      </a>
                    )}
                  {doc.checksum?.existFiles[0].level === AclLevel.BROWSE && (
                    <p
                      className={doc.checksum?.existFiles[0].fileName.length > 37 ? 'trunk-link hover' : 'hover'}
                      title={doc.checksum?.existFiles[0].fileName}
                    >
                      {doc?.checksum?.existFiles[0]?.fileName}
                    </p>
                  )}
                  {doc.checksum?.existFiles[0].level === AclLevel.NONE && (
                    <p>{translate('INSUFFICIENT_ACCESS_RIGHTS')}</p>
                  )}
                </div>
              )}
              <div>
                <span className="warning-file-upload"></span>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="progress progress-striped">
        <div
          className="progress-bar w100p"
          role="progressbar"
          aria-valuenow={100}
          aria-valuemin={0}
          aria-valuemax={100}
        ></div>
      </div>
    </div>
  )
}

interface SortableListWithDragSupportProps {
  selectedFiles: FileWithMetadata[]
  setSelectedFiles: React.Dispatch<React.SetStateAction<FileWithMetadata[]>>
  removeFile: (index: number) => void
  preview: (doc: FileWithMetadata) => void
  isUploadAndFile?: boolean
}

const SortableListWithDragSupport: React.FC<SortableListWithDragSupportProps> = ({
  selectedFiles,
  setSelectedFiles,
  removeFile,
  preview,
  isUploadAndFile
}) => {
  const context = useServiceContext()

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5 // Only require 5px movement to start drag (no delay for desktop)
      }
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200, // 200ms delay for mobile to distinguish between scroll and drag
        tolerance: 5
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = selectedFiles.findIndex(
        (file) => (file.id || `file-${selectedFiles.indexOf(file)}`) === active.id
      )
      const newIndex = selectedFiles.findIndex((file) => (file.id || `file-${selectedFiles.indexOf(file)}`) === over.id)

      const newFiles = arrayMove(selectedFiles, oldIndex, newIndex)
      setSelectedFiles(newFiles)

      // Update Formik form field if available
      if (context.store.formRef) {
        context.store.formRef.setFieldValue('selectedFiles', newFiles)
      }
    }
  }

  if (!selectedFiles || selectedFiles.length === 0) {
    return null
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={selectedFiles.map((file, index) => file.id || `file-${index}`)}
        strategy={verticalListSortingStrategy}
      >
        <div>
          {selectedFiles.map((doc, index) => (
            <SortableDocumentItem
              key={doc.id || `file-${index}`}
              doc={doc}
              index={index}
              isUploadAndFile={isUploadAndFile}
              removeFile={removeFile}
              preview={preview}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

export default SortableListWithDragSupport
